import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { Button, Layout, Spin } from "antd";
import moment from "moment";
import { Slide } from "react-awesome-reveal";
import { toast } from "react-toastify";

import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import PaymentPendingBanner from "../../grace-period/components/PaymentPendingBanner";
import MyAccountSideMenu from "../MyAccountSideMenu";
import UsageMetricsCreditSuccessModal from "./components/UsageMetricsCreditSuccessModal";
import UsageMetricsPlanBuilder from "./components/UsageMetricsPlanBuilder";
import UsageMetricsTestActivity from "./components/UsageMetricsTestActivity";
import { USAGE_METRICS_PATH, hasUsageMetricsBuildPlanParam } from "./constants";
import PostpayDashboard from "./postpay/PostpayDashboard";
import PrepaidDashboard from "./prepaid/PrepaidDashboard";
import PrepaidEmptyState from "./prepaid/PrepaidEmptyState";
import SubscriptionDashboard from "./subscription/SubscriptionDashboard";

import { getCustomerConfig, getUserCredentials } from "../../../utils/common";
import { canMakePayment } from "../../../utils/Permissions";
import {
  areSubscriptionCreditsExhausted,
  getTotalCreditsBalance,
  hasSubscriptionUsageReachedTopUpThreshold,
  mapCreditsToCategoryUsage,
  queueStartTopupPoolAlert,
  resolveUsageBillingMode,
} from "./utils";

import { fetchUsageCredits, getDefaultCreditsDateRange } from "../../../actions/usage";

import { ReactComponent as ArrowUpCircleIcon } from "../../../assets/images/subscriptions/arrow-up-circle-line.svg";
import { ReactComponent as FlowersIllustration } from "../../../assets/images/subscriptions/Flowers.svg";

import "./UsageMetricsPage.css";

const ALL_LOCATIONS_KEY = "all";

const getInitialBillingMode = () => {
  const mode = resolveUsageBillingMode(getCustomerConfig()?.customer_type);
  if (mode === "subscription" || mode === "postpay") return mode;
  return "prepaid";
};

const formatCreditsPeriodLabel = (dateRange) => {
  if (!dateRange || !dateRange.includes(",")) return "";
  const [start, end] = dateRange.split(",");
  if (!start || !end) return "";
  return `${moment(start).format("MMM D, YYYY")} - ${moment(end).format("MMM D, YYYY")}`;
};

const buildLocationOptions = (locationsList = []) => ([
  { key: ALL_LOCATIONS_KEY, label: "All locations" },
  ...locationsList.map((loc) => ({
    key: loc.location_name,
    label: loc.location_name,
  })),
]);

const hasPositiveCredit = (value) => {
  const amount = Number(value);
  return !Number.isNaN(amount) && amount > 0;
};

const responseHasPrepaidActivity = (response) => (
  hasPositiveCredit(response.total_credits)
  || (response.credits || []).some((credit) => (
    hasPositiveCredit(credit.total_credits)
    || hasPositiveCredit(credit.topup_consumed_credits)
    || hasPositiveCredit(credit.subscription_consumed_credits)
  ))
);

export default function UsageMetricsPage({
  history, location,
}) {
  const locationSearch = location?.search ?? "";
  const [billingMode, setBillingMode] = useState(getInitialBillingMode); // "prepaid" | "subscription" | "postpay"
  const [isBuildingPlan, setIsBuildingPlan] = useState(
    hasUsageMetricsBuildPlanParam(locationSearch),
  );
  const [isViewingActivity, setIsViewingActivity] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(() => getInitialBillingMode() !== "subscription");
  const [balance, setBalance] = useState(0);
  const [categoryUsage, setCategoryUsage] = useState([]);
  const [creditSuccess, setCreditSuccess] = useState({ open: false, credits: 0 });
  const [selectedLocationKey, setSelectedLocationKey] = useState(ALL_LOCATIONS_KEY);
  const [creditsDateRange, setCreditsDateRange] = useState(getDefaultCreditsDateRange);
  const [locationsList, setLocationsList] = useState([]);
  const [isFilterRefreshing, setIsFilterRefreshing] = useState(false);
  const [hasPrepaidActivity, setHasPrepaidActivity] = useState(false);
  const hasLoadedCreditsOnce = useRef(false);

  const allowPayment = canMakePayment();
  const showTopUpButton = allowPayment && billingMode !== "postpay" && (
    billingMode !== "subscription"
    || hasSubscriptionUsageReachedTopUpThreshold(categoryUsage)
  );
  const showDashboardHeader = !isBuildingPlan && !isViewingActivity;
  const locationOptions = useMemo(
    () => buildLocationOptions(locationsList),
    [locationsList],
  );
  const periodLabel = useMemo(
    () => formatCreditsPeriodLabel(creditsDateRange),
    [creditsDateRange],
  );

  const clearBuildPlanParam = () => {
    if (hasUsageMetricsBuildPlanParam(locationSearch)) {
      history.replace(USAGE_METRICS_PATH);
    }
  };

  useEffect(() => {
    if (!hasUsageMetricsBuildPlanParam(locationSearch)) {
      return;
    }

    if (allowPayment && billingMode !== "postpay") {
      setIsBuildingPlan(true);
      return;
    }

    history.replace(USAGE_METRICS_PATH);
  }, [allowPayment, billingMode, history, locationSearch]);

  const loadCredits = async (signal = null, { isFilterChange = false } = {}) => {
    if (isFilterChange) {
      setIsFilterRefreshing(true);
    } else {
      setCreditsLoading(true);
    }

    const user = getUserCredentials() || {};
    const isAllLocations = selectedLocationKey === ALL_LOCATIONS_KEY;
    const response = await fetchUsageCredits({
      locationName: isAllLocations ? user.company_name : selectedLocationKey,
      dateRange: creditsDateRange,
      allLocation: isAllLocations,
      signal,
    });

    if (response.canceled) return;

    if (!response.success) {
      toast.error(response.message);
      setCreditsLoading(false);
      setIsFilterRefreshing(false);
      return;
    }

    setBillingMode(resolveUsageBillingMode(response.customer_type));
    setBalance(getTotalCreditsBalance(response.total_credits));
    if (responseHasPrepaidActivity(response)) {
      setHasPrepaidActivity(true);
    }
    setCategoryUsage(mapCreditsToCategoryUsage(response.credits));
    setLocationsList(response.locations_list);
    hasLoadedCreditsOnce.current = true;
    setCreditsLoading(false);
    setIsFilterRefreshing(false);
  };

  useEffect(() => {
    // Subscription dashboard owns the credits request for this billing mode.
    if (billingMode === "subscription") {
      setCreditsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    loadCredits(controller.signal, {
      isFilterChange: hasLoadedCreditsOnce.current,
    });

    return () => {
      controller.abort();
    };
  }, [selectedLocationKey, creditsDateRange, billingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectLocation = (locationKey) => {
    setSelectedLocationKey(locationKey);
  };

  const handlePeriodChange = (startDate, endDate) => {
    if (!startDate && !endDate) {
      setCreditsDateRange(getDefaultCreditsDateRange());
      return;
    }
    setCreditsDateRange(`${startDate},${endDate || startDate}`);
  };

  const handlePlanBuilderBack = () => {
    setIsBuildingPlan(false);
    clearBuildPlanParam();
    if (billingMode !== "subscription") {
      loadCredits(null, { isFilterChange: true });
    }
  };

  const handleTestActivityBack = () => {
    setIsViewingActivity(false);
    if (billingMode !== "subscription") {
      loadCredits(null, { isFilterChange: true });
    }
  };

  const handleRechargeSuccess = async (response = {}) => {
    setIsBuildingPlan(false);
    clearBuildPlanParam();
    if (!response.skipSuccessModal) {
      setCreditSuccess({
        open: true,
        credits: Number(response.creditedTests),
      });
    }
    if (
      billingMode === "subscription"
      && response.topupPlan
      && areSubscriptionCreditsExhausted(categoryUsage)
    ) {
      queueStartTopupPoolAlert({
        poolTests: Number(response.creditedTests),
      });
    }
    if (billingMode !== "subscription") {
      await loadCredits(null, { isFilterChange: true });
    }
  };

  const handleCreditSuccessClose = () => {
    setCreditSuccess({ open: false, credits: 0 });
  };

  const handleMenuClick = ({ key }) => {
    const routes = {
      1: "/myaccount/profile",
      2: "/myaccount/members",
      3: "/myaccount/usage-metrics",
      4: "/myaccount/invoice",
      5: "/myaccount/help",
    };

    if (routes[key]) {
      history.push(routes[key]);
    }
  };

  return (
    <div className="myAccountMainContainer">
      <Layout className="UsageMetrics__Layout">
        <MyAccountSideMenu
          selectedMenuItem="3"
          handleMenuClick={handleMenuClick}
        />
        <main
          className={`UsageMetrics__Content${isBuildingPlan ? " UsageMetrics__Content--planBuilder" : ""}`}
        >
          {isBuildingPlan && (
            <>
              <FlowersIllustration
                className="UsageMetrics__Flower UsageMetrics__Flower--bottomLeft"
                aria-hidden
              />
              <FlowersIllustration
                className="UsageMetrics__Flower UsageMetrics__Flower--topRight"
                aria-hidden
              />
            </>
          )}
          {isViewingActivity ? (
            <div className="UsageMetrics__ActivityView">
              <Slide direction="up" triggerOnce>
                <UsageMetricsTestActivity onBack={handleTestActivityBack} />
              </Slide>
            </div>
          ) : (
            <StyledScrollbar
              className="UsageMetrics__ScrollBar"
              noScrollX
            >
              {showDashboardHeader && (
                <>
                  <Slide direction="left" triggerOnce>
                    <div>
                      <header className="UsageMetrics__Header">
                        <div>
                          <h1 className="UsageMetrics__Title">Usage Metrics</h1>
                          <p className="UsageMetrics__Description">
                            {billingMode === "postpay"
                              ? "Track your available tests, usage, purchases, and refunds across test categories."
                              : "Track your available credits, usage, purchases, and refunds across test categories."}
                          </p>
                        </div>
                        {!creditsLoading && (billingMode === "subscription" || billingMode === "postpay" || hasPrepaidActivity) && (
                          <div className="UsageMetrics__Actions">
                            <Button
                              onClick={() => setIsViewingActivity(true)}
                            >
                              Test Activity
                            </Button>
                            {showTopUpButton && (
                              <Button
                                type="primary"
                                icon={<ArrowUpCircleIcon />}
                                onClick={() => setIsBuildingPlan(true)}
                              >
                                Top Up Tests
                              </Button>
                            )}
                          </div>
                        )}
                      </header>
                    </div>
                  </Slide>
                  {!creditsLoading && (
                    <Slide direction="up" triggerOnce>
                      <PaymentPendingBanner dismissible={false} variant="inset" />
                    </Slide>
                  )}
                </>
              )}

              {creditsLoading && showDashboardHeader && (
                <div className="UsageMetrics__Loading">
                  <Spin size="large" />
                </div>
              )}

              {isBuildingPlan && allowPayment && billingMode !== "postpay" && (
                <Slide direction="up" triggerOnce>
                  <UsageMetricsPlanBuilder
                    mode={billingMode}
                    onBack={handlePlanBuilderBack}
                    onRechargeSuccess={handleRechargeSuccess}
                  />
                </Slide>
              )}
              {!isBuildingPlan && !creditsLoading && billingMode === "subscription" && (
                <Slide direction="up" triggerOnce>
                  <SubscriptionDashboard
                    onTopupTests={() => setIsBuildingPlan(true)}
                    locations={locationOptions}
                    selectedLocationKey={selectedLocationKey}
                    onSelectLocation={handleSelectLocation}
                    onLocationsLoaded={setLocationsList}
                    onCategoryCreditsLoaded={setCategoryUsage}
                    suppressStartTopupPoolAlert={creditSuccess.open}
                  />
                </Slide>
              )}
              {!creditsLoading && !isBuildingPlan && billingMode === "postpay" && (
                <Slide direction="up" triggerOnce>
                  <PostpayDashboard
                    categoryUsage={categoryUsage}
                    locations={locationOptions}
                    selectedLocationKey={selectedLocationKey}
                    onSelectLocation={handleSelectLocation}
                    periodLabel={periodLabel}
                    onPeriodChange={handlePeriodChange}
                    loading={isFilterRefreshing}
                  />
                </Slide>
              )}
              {!creditsLoading && !isBuildingPlan && hasPrepaidActivity && billingMode === "prepaid" && (
                <Slide direction="up" triggerOnce>
                  <PrepaidDashboard
                    categoryUsage={categoryUsage}
                    locations={locationOptions}
                    selectedLocationKey={selectedLocationKey}
                    onSelectLocation={handleSelectLocation}
                    periodLabel={periodLabel}
                    onPeriodChange={handlePeriodChange}
                    dateRange={creditsDateRange}
                    loading={isFilterRefreshing}
                  />
                </Slide>
              )}
              {!creditsLoading && !isBuildingPlan && !hasPrepaidActivity && billingMode === "prepaid" && (
                <Slide direction="up" triggerOnce>
                  <PrepaidEmptyState
                    balance={balance}
                    onPurchase={allowPayment ? () => setIsBuildingPlan(true) : undefined}
                  />
                </Slide>
              )}
            </StyledScrollbar>
          )}
          <UsageMetricsCreditSuccessModal
            open={creditSuccess.open}
            credits={creditSuccess.credits}
            onClose={handleCreditSuccessClose}
          />
        </main>
      </Layout>
    </div>
  );
}
